import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { CustomizationTabsService, UserTabDto } from '@myarvato/core/customization';
import { UserTrackingService } from '@myarvato/core/services';
import { CustomizationTranslationService } from '@myarvato/shared/services';
import { Router } from '@angular/router';
import { DataView } from 'primeng/dataview';
import { TransportUserTabDataDto } from '@myarvato/modules/transport/models';
import { Message } from 'primeng/message';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'transport-watched-shipments',
  imports: [SkeletonModule, DataView, Message, TranslatePipe, ButtonModule],
  templateUrl: './watched-shipments.component.html',
  styleUrl: './watched-shipments.component.scss',
})
export class WatchedShipmentsComponent implements OnInit {
  // Injections
  private readonly customizationTabsService = inject(CustomizationTabsService<TransportUserTabDataDto>);
  private readonly router = inject(Router);
  private readonly userTrackingService = inject(UserTrackingService);
  private readonly customizationTranslationService = inject(CustomizationTranslationService);

  // Fields
  protected readonly context = 'transport';
  protected readonly trackingCategory = 'Transport - Overview - Watched Shipments';

  // Change detection
  protected readonly state = signal<{
    loading: boolean;
    error: unknown | undefined;
    data: UserTabDto<TransportUserTabDataDto>[] | undefined;
  }>({
    loading: true,
    error: undefined,
    data: undefined,
  });

  private readonly tabsContext = 'transport-tabs-view';

  protected readonly isOnError = computed(() => this.state().error !== undefined);
  protected readonly isSuccess = computed(() => this.state().data !== undefined && !this.isOnError() && !this.isEmpty());
  protected readonly isLoading = computed(() => this.state().loading);
  protected readonly isEmpty = computed(() => this.state().data !== undefined && (this.state()?.data?.length ?? 0) < 1);
  protected readonly data = computed(() => this.state().data ?? []);
  protected readonly reversedData = computed(() => [...this.data()].reverse());

  protected readonly errorMessage = computed(() => {
    const exception = this.state().error as { error: { errorCode: string } };
    const errorCode = exception?.error?.errorCode || 'G0400';
    const errorTranslationTitle = this.customizationTranslationService.getTranslation(`errors.${errorCode}.title`, 'errors.G0400.title');
    const errorTranslationDescription = this.customizationTranslationService.getTranslation(
      `errors.${errorCode}.description`,
      'errors.G0400.description',
    );

    return {
      title: errorTranslationTitle,
      description: errorTranslationDescription,
    };
  });

  public ngOnInit(): void {
    this.customizationTabsService.getTabs(this.tabsContext).subscribe({
      next: (data) => {
        this.state.set({ loading: false, error: undefined, data: data });
      },
      error: (error) => {
        console.error(error);
        this.state.set({ loading: false, error: error, data: undefined });
      },
    });
  }

  protected goToDetailsPage() {
    this.userTrackingService.trackEvent(this.trackingCategory, 'Go to Tracking Details clicked');
    this.router.navigate(['transport', 'tabs']).then();
  }

  protected goToWatchedShipment(tabObject: UserTabDto<TransportUserTabDataDto>) {
    this.router
      .navigate(['transport', 'tabs'], {
        state: tabObject,
      })
      .then();
  }
}
